import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { CODES, isApiError, isNetworkError } from '../api/errors';
import { brandOf } from '../app/brand';
import { useRuntime, useSession } from '../app/hooks';
import { useTitleSection } from '../app/documentTitle';
import { logUnexpected } from '../app/unexpected';
import { BrandLockup } from '../ui/BrandLockup';

const MIN_LENGTH = 12;

export function PasswordScreen() {
  const { t } = useTranslation();
  // INC-14a-3a-1: the tab names this screen before the organization, and no product.
  useTitleSection(t('password.title'), 'outside');
  const { session, engine } = useRuntime();
  const snapshot = useSession();
  const brand = brandOf(snapshot);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (snapshot.status === 'authenticated') return <Navigate to="/" replace />;
  if (snapshot.status === 'anonymous') return <Navigate to="/login" replace />;

  const submit = async (event: { preventDefault(): void }): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await session.changePassword(current, next);
      engine.trigger('manual');
    } catch (failure) {
      if (isNetworkError(failure)) setError(t('errors.networkError'));
      else if (isApiError(failure) && failure.code === CODES.passwordRejected) {
        setError(t('password.rejected', { detail: failure.detail }));
      } else if (isApiError(failure) && failure.code === CODES.invalidCredentials) {
        setError(t('password.wrongCurrent'));
      } else {
        // INC-15b-3 (C, point 180): in words, and its code to the log alone.
        logUnexpected('change password', failure);
        setError(t('password.unexpectedError'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="access" data-testid="password-screen">
      {/* INC-12a (A.4): the organization above, as on the access screen. */}
      <BrandLockup
        name={brand.name === '' ? t('access.neutralTitle') : brand.name}
        logoUrl={brand.logoUrl}
        markUrl={brand.markUrl}
      />
      <form className="card stack" onSubmit={(event) => void submit(event)}>
        <h1 style={{ margin: 0 }}>{t('password.title')}</h1>
        <p className="muted">{t('password.explanation')}</p>
        <div className="field">
          <label htmlFor="current-password">{t('password.current')}</label>
          <input
            id="current-password"
            className="input"
            type="password"
            autoComplete="current-password"
            value={current}
            required
            onChange={(event) => {
              setCurrent(event.target.value);
            }}
          />
        </div>
        <div className="field">
          <label htmlFor="new-password">{t('password.new')}</label>
          <input
            id="new-password"
            className="input"
            type="password"
            autoComplete="new-password"
            value={next}
            required
            minLength={MIN_LENGTH}
            onChange={(event) => {
              setNext(event.target.value);
            }}
          />
          <span className="muted">{t('password.policy', { min: MIN_LENGTH })}</span>
        </div>
        {error !== null ? (
          <div className="alert error" role="alert">
            {error}
          </div>
        ) : null}
        <button type="submit" className="btn block" disabled={busy}>
          {t('password.submit')}
        </button>
      </form>
    </div>
  );
}
